import {useContext, useEffect, useRef, useState} from 'react';
import styles from './MemoryMessage.module.css';
import {CV4RMemory} from '../../../../../../common-lib/src/api/Api.types';
import cn from 'classnames';
import {api} from '../../../../../../common-lib/src/api/Api';
import deleteImg from './images/delete-red.svg';
import editImg from './images/edit.svg';
import copyImg from './images/copy.svg';
import {enqueueSnackbar} from 'notistack';
import {Spinner} from '../../../../../../common-lib/src/components';
import {highlightText} from '../utils';
import {AppContext} from '../../../../../../App';
import {useConfirmation} from '../../../../../../common-lib/src/hooks/useConfirmation/useConfirmation';

type Props = {
	memory: CV4RMemory;
	searchQuery?: string;
	onEdit: (memory: CV4RMemory) => void;
	onDelete: (memory: CV4RMemory) => void;
};

type MemoryActions = {
	handleDelete: () => void;
	handleEdit: () => void;
	handleCopy: () => void;
};
export const useCommonActions = (
	memory: CV4RMemory,
	actions: MemoryActions
) => {
	const isTouchDevice = 'ontouchstart' in window;
	const [isHoverOpen, setIsHoverOpen] = useState(false);

	const shouldShowHover =
		!!actions && Object.keys(actions).length && !isTouchDevice;
	const shouldShowHoverOnClick =
		!!actions && Object.keys(actions).length && isTouchDevice;

	const handleDelete = () => {
		actions?.handleDelete();
		setIsHoverOpen(false);
		document.removeEventListener('click', handleClosePopover);
	};

	const handleClosePopover = (e: MouseEvent) => {
		const messageEl = (e.target as HTMLElement)?.closest(
			'.memory_delete-block'
		);
		const messageId = messageEl?.id;
		const currentMessageId = `memory_${memory.id}`;
		if (messageId && messageId !== currentMessageId) {
			setIsHoverOpen(false);
		}
		if (!(e.target as HTMLElement)?.closest('.memory_delete-block')) {
			setIsHoverOpen(false);
		}
	};

	useEffect(() => {
		if (isHoverOpen) {
			document.addEventListener('click', handleClosePopover);
		} else {
			document.removeEventListener('click', handleClosePopover);
		}
		return () => {
			document.removeEventListener('click', handleClosePopover);
		};
	}, [isHoverOpen]);

	const handleTouchStart = (id: string) => {
		if (shouldShowHoverOnClick) {
			setIsHoverOpen(true);
		}
	};

	return {
		shouldShowHover,
		shouldShowHoverOnClick,
		handleDelete,
		handleTouchStart,
		isHoverOpen,
	};
};

export const MemoryMessage = ({
	memory,
	onEdit,
	onDelete,
	searchQuery,
}: Props) => {
	const {user, setIsLoginOpened, setPaywallType, setIsPaywallModalOpened} =
		useContext(AppContext);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editMessage, setEditMessage] = useState(memory.text);
	const memoryRef = useRef<HTMLDivElement>(null);
	const [memoryHeight, setMemoryHeight] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (memoryRef.current) {
			setMemoryHeight(memoryRef.current.clientHeight);
		}
	}, [memoryRef.current]);

	const handleEdit = () => {
		if (!user || user?.isAnon) {
			setIsLoginOpened(true);
			return;
		}
		if (user?.accessLevel !== 'ultra') {
			setPaywallType('ultra');
			setIsPaywallModalOpened(true);
			return;
		}
		setIsEditMode(true);
	};
	const handleTurnOffEditMode = () => {
		if (editMessage.trim() === '') {
			setEditMessage(memory.text);
		}
		setIsEditMode(false);
	};

	const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setEditMessage(e.target.value);
	};

	const handleSubmit = async () => {
		if (editMessage.trim() === '') {
			setEditMessage(memory.text);
			return;
		}

		handleTurnOffEditMode();

		setIsLoading(true);
		try {
			await api.updateMemoryItem(memory.id, editMessage);
			onEdit?.(memory);
		} catch (error) {
			enqueueSnackbar('Failed to update memory', {variant: 'error'});
		} finally {
			setIsLoading(false);
		}
	};

	const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter') {
			handleSubmit();
			return;
		}
		if (e.key === 'Escape') {
			handleTurnOffEditMode();
			return;
		}
	};

	useEffect(() => {
		if (isEditMode) {
			setEditMessage(memory.text);
		}
	}, [isEditMode, memory.text]);
	const handleCopy = () => {
		enqueueSnackbar('Text copied to clipboard', {variant: 'success'});
		navigator.clipboard.writeText(memory.text);
	};

	const handleConfirmDelete = async () => {
		setIsLoading(true);
		try {
			await api.deleteMemoryItem(memory.id);
			onDelete?.(memory);
		} catch (error) {
			enqueueSnackbar('Failed to delete memory', {variant: 'error'});
		} finally {
			setIsLoading(false);
		}
	};

	const {open, ConfirmationDialog} = useConfirmation({
		title: 'Are you sure?',
		text: 'Are you sure you want to delete this memory?',
		okButtonText: 'Delete',
		onSubmit: handleConfirmDelete,
	});

	const handleDelete = async () => {
		if (!user || user?.isAnon) {
			setIsLoginOpened(true);
			return;
		}
		if (user?.accessLevel !== 'ultra') {
			setPaywallType('ultra');
			setIsPaywallModalOpened(true);
			return;
		}

		open();
	};

	const actions = {
		handleDelete,
		handleEdit,
		handleCopy,
	};
	const {isHoverOpen} = useCommonActions(memory, actions);

	if (isEditMode) {
		return (
			<>
				<div
					className={cn(styles.memoryMessageEdit)}
					style={{
						minHeight: memoryHeight,
					}}
				>
					<textarea
						className="edit-message"
						defaultValue={editMessage}
						onKeyDown={handleKeyDown}
						onChange={handleChangeText}
						style={{
							minHeight: memoryHeight,
						}}
					/>
				</div>
				<div className={cn(styles.memoryMessageEditRow)}>
					<button
						className={cn(
							styles.memoryMessageEditBtn,
							styles.memoryMessageEditBtnCancel
						)}
						type="button"
						onClick={handleTurnOffEditMode}
					>
						Cancel
					</button>
					<button
						className={cn(
							styles.memoryMessageEditBtn,
							styles.memoryMessageEditBtnSave
						)}
						type="button"
						onClick={handleSubmit}
					>
						Save
					</button>
				</div>
			</>
		);
	}

	if (isLoading) {
		return (
			<div className={styles.memoryMessageContainer} ref={memoryRef}>
				<Spinner withLayout={false} />
			</div>
		);
	}

	return (
		<div className={styles.memoryMessageContainer} ref={memoryRef}>
			<div
				className={cn(styles.memoryMessage)}
				id={`memory_${memory.id}`}
				dangerouslySetInnerHTML={{
					__html: searchQuery
						? highlightText(memory.text, searchQuery)
						: memory.text,
				}}
			/>
			<MemoryDeleteBlock
				memory={memory}
				actions={actions}
				isHoverOpen={isHoverOpen}
				handleDelete={handleDelete}
			/>
			<ConfirmationDialog />
		</div>
	);
};

const MemoryDeleteBlock = ({
	memory,
	actions,
	isHoverOpen,
}: {
	memory: CV4RMemory;
	actions: MemoryActions;
	isHoverOpen: boolean;
	handleDelete: () => void;
}) => {
	return (
		<div
			className={cn(styles.memoryActionsBlock, {
				[styles.memoryActionsBlockShow]: isHoverOpen,
			})}
		>
			<div
				className="chat__message_delete-block__row"
				onClick={actions.handleEdit}
			>
				<img
					src={editImg}
					alt="Edit message"
					className={styles.memoryActionIcon}
				/>
			</div>
			<div
				className="chat__message_delete-block__row"
				onClick={actions.handleCopy}
			>
				<img
					src={copyImg}
					alt="Copy message"
					className={styles.memoryActionIcon}
				/>
			</div>
			<div
				className="chat__message_delete-block__row"
				onClick={actions.handleDelete}
			>
				<img
					src={deleteImg}
					alt="Delete message"
					className={styles.memoryActionIcon}
				/>
			</div>
		</div>
	);
};

type MemoryGroup = {
	date: string;
	memories: CV4RMemory[];
};

type MemoriesProps = {
	memory: CV4RMemory[];
	handleEdit: (memory: CV4RMemory) => void;
	handleDelete: (memory: CV4RMemory) => void;
	searchQuery?: string;
};

export const Memories = ({
	memory,
	handleEdit,
	handleDelete,
	searchQuery,
}: MemoriesProps) => {
	const sortedMemories = [...memory].sort(
		(a, b) =>
			new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
	);

	const groupedMemories = sortedMemories.reduce<MemoryGroup[]>(
		(groups, mem) => {
			const date = new Date(mem.created_at);
			const formattedDate = new Intl.DateTimeFormat('en-US', {
				month: 'long',
				day: 'numeric',
			}).format(date);

			const existingGroup = groups.find((g) => g.date === formattedDate);
			if (existingGroup) {
				existingGroup.memories.push(mem);
			} else {
				groups.push({
					date: formattedDate,
					memories: [mem],
				});
			}
			return groups;
		},
		[]
	);

	return (
		<div className={styles.memories}>
			{groupedMemories.map((group, groupIndex) => (
				<div key={groupIndex} className={styles.memoryGroup}>
					<div className={styles.memoryGroupDate}>{group.date}</div>
					{group.memories.map((mem, index) => (
						<MemoryMessage
							key={index}
							memory={mem}
							onEdit={handleEdit}
							onDelete={handleDelete}
							searchQuery={searchQuery}
						/>
					))}
				</div>
			))}
		</div>
	);
};
